import React from 'react';
import { motion } from 'motion/react';
import { 
  UserPlus, 
  Lightbulb, 
  PenTool, 
  Calendar, 
  Send,
  ArrowRight,
  StepForward,
  CheckCircle2
} from 'lucide-react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const steps = [
  {
    icon: UserPlus,
    title: 'step1', // Placeholder for key
    image: "/platapost-brandbrain.jpg"
  },
  {
    icon: Lightbulb,
    title: 'step2',
    image: "/platapost-social-media-content-calendar.jpg"
  },
  {
    icon: PenTool,
    title: 'step3',
    image: "/platapost-editing.jpg"
  },
  {
    icon: Calendar,
    title: 'step4',
    image: "/platapost-content-scheduling.jpg"
  }
];

const HowItWorks = () => {
  const { t } = useTranslation();
  
  const formattedSteps = [
    {
      ...steps[0],
      title: t.steps.step1.title,
      desc: t.steps.step1.desc,
      behind: t.steps.step1.behind,
    },
    {
      ...steps[1],
      title: t.steps.step2.title,
      desc: t.steps.step2.desc,
      behind: t.steps.step2.behind,
    },
    {
      ...steps[2],
      ...steps[2],
      title: t.steps.step3.title,
      desc: t.steps.step3.desc,
      behind: t.steps.step3.behind,
      examples: t.steps.step3.examples,
    },
    {
      ...steps[3],
      title: t.steps.step4.title,
      desc: t.steps.step4.desc,
      behind: t.steps.step4.behind,
    }
  ];

  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title={`${t.howItWorks} - PlataPost`}
        description={t.howItWorksSubtitle}
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-24 space-y-4">
          <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">
            {t.howItWorksProcess}
          </p>
          <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">
            {t.howItWorks.split(' ')[0]} <span className="text-indigo-500">{t.howItWorks.split(' ').slice(1).join(' ')}.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto font-medium">
            {t.howItWorksSubtitle}
          </p>
        </div>

        <div className="space-y-40 mb-40">
          {formattedSteps.map((step: any, i) => (
            <div key={i} className={`flex flex-col lg:flex-row items-center gap-20 ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className="flex-1 space-y-8">
                <div className="inline-flex items-center gap-4 px-5 py-2 rounded-full glass-card border border-white/5 text-indigo-400 text-xs font-black uppercase tracking-widest">
                   <span className="w-6 h-6 rounded-lg bg-indigo-600 text-white flex items-center justify-center shadow-lg shadow-indigo-600/20">
                    {i + 1}
                  </span>
                  <span>{t.stepLabel} {i + 1}</span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-black text-white leading-tight tracking-tighter">{step.title}</h2>
                <div className="space-y-6">
                  <p className="text-xl text-gray-400 leading-relaxed font-medium">
                    {step.desc}
                  </p>
                  
                  {step.examples && (
                    <div className="space-y-3 pl-6 border-l-2 border-indigo-500/30">
                      <p className="text-sm font-black text-indigo-400 uppercase tracking-widest">{t.examplesTitle}</p>
                      {step.examples.map((example, idx) => (
                        <p key={idx} className="text-gray-500 italic font-medium">{example}</p>
                      ))}
                    </div>
                  )}

                  <div className="p-6 rounded-3xl bg-white/5 border border-white/5 space-y-2">
                    <p className="text-xs font-black text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      {t.whatBehind}
                    </p>
                    <p className="text-gray-400 font-medium">
                      {step.behind}
                    </p>
                  </div>
                </div>


              </div>
              <div className="flex-1 w-full">
                <div className="aspect-[4/3] rounded-[4rem] overflow-hidden shadow-2xl relative group border border-white/5 shadow-indigo-600/5">
                  <img 
                    src={step.image} 
                    alt={step.title} 
                    className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110" 
                    onError={(e) => {
                      console.error(`Failed to load image: ${step.image}. Expected path: ${window.location.origin}${step.image}`);
                      // Fallback to a placeholder if it fails
                      (e.target as HTMLImageElement).src = `https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030303]/40 to-transparent" />
                </div>
              </div>
            </div>
          ))}
        </div>


        {/* Final Logic Section */}
        <div className="glass-card rounded-[4rem] p-12 lg:p-24 border border-white/5 relative overflow-hidden group shadow-2xl shadow-indigo-600/5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/10 blur-[100px] -mr-32 -mt-32" />
          <div className="text-center mb-20 relative z-10">
            <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight">{t.whyDifferent}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-12 relative z-10">
            {[
              { title: t.noGenericAI, desc: t.noGenericAIDesc },
              { title: t.strategyCopyCreative, desc: t.strategyCopyCreativeDesc },
              { title: t.stayInControl, desc: t.stayInControlDesc }
            ].map((item) => (
              <div key={item.title} className="text-center group/item p-8 rounded-3xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/5">
                <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-8 group-hover/item:scale-110 transition-transform border border-white/10">
                  <CheckCircle2 className="w-8 h-8 text-indigo-400" />
                </div>
                <h3 className="font-black text-2xl text-white mb-4 tracking-tight">{item.title}</h3>
                <p className="text-gray-400 text-lg leading-relaxed font-medium">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-24 text-center relative z-10 space-y-8">
            <div className="max-w-2xl mx-auto space-y-4">
              <h3 className="text-3xl lg:text-4xl font-black text-white tracking-tighter">
                {t.readyToTurn}
              </h3>
              <p className="text-xl text-gray-400 font-medium italic">
                {t.startWithBrain}
              </p>
            </div>
            <Link 
              to="/dashboard" 
              className="px-12 py-6 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30 inline-flex items-center gap-4 group"
            >
              <span>{t.getStartedNow}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>

  );
};

export default HowItWorks;
